// @flow

import * as React from 'react';

import { connect } from 'react-redux';
import classNames from '@ta/common.classnames';
import { featureIsEnabled } from '@ta/common.features';
import { localize } from '@ta/common.i18n';
import { type ViewportCategory } from '@ta/common.state';
import { SharePopover } from '@ta/public.share-cta';
import { MemberInteractionConsumer } from '@ta/social.blocks';
import ButtonGroup from '@ta/social.button-group';
import FollowButton from '@ta/social.follow';
import MessageBlock from '@ta/social.message';
import { ProfileOverflowButton } from '@ta/social.profile';
import { Icon } from '@ta/styleguide.icon';
import styles from './ProfilePublicSocialButtonBlock.less';

type DesktopProps = {
  profile: any,
  shareProps: {|
    url: string,
    itemId: string,
    itemType: string,
  |},
  isRemovedForBeta: boolean,
};

type MobileProps = {
  profile: any,
};

const SocialButtonBlockDesktop = ({ profile, shareProps, isRemovedForBeta }: DesktopProps) => (
  <SharePopover {...shareProps} shiftX={-8}>
    {(target, open) => (
      <ButtonGroup>
        {featureIsEnabled('social_block_enabled') &&
        profile.blockStatus &&
        profile.blockStatus.isBlocking ? (
          <MemberInteractionConsumer>
            {openMemberInteractionModal => (
              <button
                className={classNames('ui_button', styles.greenless)}
                onClick={() => {
                  openMemberInteractionModal('UNBLOCK');
                }}
              >
                <span className={styles.block}>
                  {/** Unblock */}
                  {localize('social_unblock_btn')}
                </span>
              </button>
            )}
          </MemberInteractionConsumer>
        ) : (
          <FollowButton size="default" greenless profile={profile} displayType="button+icon" />
        )}
        {featureIsEnabled('unified_inbox_writes_enabled') &&
          featureIsEnabled('unified_inbox_private_messaging') && (
            <MessageBlock displayType="icon" profile={profile} />
          )}
        {!isRemovedForBeta && (
          <span ref={target}>
            <button className={classNames('ui_button', styles.greenless)} onClick={open}>
              <span className={styles.cta}>
                <Icon name="repost-fill" />
              </span>
            </button>
          </span>
        )}
        {(featureIsEnabled('social_block_enabled') ||
          featureIsEnabled('social_report_profile_iap_enabled')) && (
          <ProfileOverflowButton
            displayName={profile.displayName}
            profileUserId={profile.userId}
            isBlocking={profile.blockStatus && profile.blockStatus.isBlocking}
          />
        )}
      </ButtonGroup>
    )}
  </SharePopover>
);
SocialButtonBlockDesktop.defaultProps = {
  isRemovedForBeta: true,
};

const SocialButtonBlockMobile = ({ profile }: MobileProps) => (
  <React.Fragment>
    <div className="ui_columns is-mobile">
      <div className="ui_column is-6">
        {profile.blockStatus && profile.blockStatus.isBlocking ? (
          featureIsEnabled('social_block_enabled') && (
            <MemberInteractionConsumer>
              {openMemberInteractionModal => (
                <button
                  className={classNames('ui_button', 'fullwidth', styles.greenless)}
                  onClick={() => {
                    openMemberInteractionModal('UNBLOCK');
                  }}
                >
                  <span className={styles.block}>
                    {/** Unblock */}
                    {localize('social_unblock_btn')}
                  </span>
                </button>
              )}
            </MemberInteractionConsumer>
          )
        ) : (
          <FollowButton size="fullwidth" greenless profile={profile} displayType="button+icon" />
        )}
      </div>
      <div className="ui_column is-6">
        {featureIsEnabled('unified_inbox_private_messaging') && (
          <MessageBlock displayType="button+icon" profile={profile} size="fullwidth" />
        )}
      </div>
    </div>
  </React.Fragment>
);

type Props = {
  ...MobileProps,
  viewportCategory: ViewportCategory,
};

const ProfilePublicSocialButtonBlock = connect<Props, MobileProps, _, _, _, _>(state => ({
  viewportCategory: state.meta.device.viewportCategory,
}))(({ viewportCategory, profile }: { viewportCategory: string, profile: any }) => {
  const shareProps = {
    url: `/Profile/${encodeURIComponent(profile.username)}`,
    itemId: profile.id,
    itemType: 'Profile',
  };
  return viewportCategory === 'MOBILE' ? (
    <SocialButtonBlockMobile profile={profile} />
  ) : (
    <SocialButtonBlockDesktop profile={profile} shareProps={shareProps} />
  );
});

export default ProfilePublicSocialButtonBlock;
